import React, { FormEvent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import YearPicker from "components/__reusable/YearPicker";
import TextInput from "components/__reusable/TextInput";
import FormActionBar from "components/__reusable/FormActionBar";
import useAppSelector from "hooks/useAppSelector";
import type { ClassFormData } from "types/Class";

const TODAY = new Date();

interface FormProps {
  isUpdate: boolean;
  data: ClassFormData | null;
  onSubmit: (formData: ClassFormData) => Promise<void>;
}

const Form: React.FC<FormProps> = ({ isUpdate, data, onSubmit }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [academicYear, setAcademicYear] = useState(
    TODAY.getFullYear().toString(),
  );
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setAcademicYear(data.year);
  }, [data]);

  const renderInput = (label: string, inputElement: ReactNode) => {
    return (
      <div className="lg:flex items-start justify-start space-y-3 lg:space-x-3 lg:space-y-0">
        <h3 className="lg:w-1/4 font-medium lg:pt-2">{label}</h3>
        {inputElement}
      </div>
    );
  };

  const { loading } = useAppSelector((state) => state.class);
  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit({ name, year: academicYear });
    if (!isUpdate) {
      setName("");
      setAcademicYear(TODAY.getFullYear().toString());
    }
  };

  return (
    <form className="space-y-3 lg:space-y-5" onSubmit={submitForm}>
      {renderInput(
        t("_class_form._academic_year"),
        <div className="w-1/2">
          <YearPicker
            value={academicYear}
            onChange={(value) => setAcademicYear(value)}
          />
        </div>,
      )}
      {renderInput(
        t("_class_form._class_name"),
        <div className="w-1/2">
          <TextInput
            name={"className"}
            placeholder={t("_class_form._class_name")}
            disabled={false}
            value={name}
            onChange={(value) => setName(value)}
          />
        </div>,
      )}
      <div>
        <p>{t("_class_form._class_name_change_info")}</p>
      </div>
      <FormActionBar
        primaryLabel={t("_class_form._submit")}
        primaryDisabled={name === ""}
        primaryLoading={loading}
        variant={isUpdate ? "sticky" : "inline"}
      />
    </form>
  );
};

export default Form;
